import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrDarkModal from 'client/components/SrDarkModal';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrModalContent from 'client/components/SrModalContent';
import SrFlex from 'client/components/SrFlex';
import SrModalDocsSplitView from 'client/components/SrModalDocsSplitView/SrModalDocsSplitView';
import SrUtilityProfitDeleteDocsConfirmationModal from 'client/components/SrUtilityProfitDeleteDocsConfirmationModal/SrUtilityProfitDeleteDocsConfirmationModal';
import { isHomebuyerPartner } from 'common/utility/helpers';

const getFormValues = ({
  utilityPartner,
  utilityPartnerAddressId,
  utilityTypes,
  setDocumentViewModalArgs,
  setRemoveDocumentModalArgs,
  formikBag = {},
  values = {},
}) => {
  return [
    {
      id: 'docsForTenant',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Upload docs for your client:'
        : 'Upload docs for tenant:',
      type: 'file_multi_upload_date',
      properties: {
        filePath: `/utilityPartnerAddress/${utilityPartnerAddressId}/requiredUtilityTenantDocs/{uuid}`,
        fileAccept: '*',
        uploadIdPrefix: utilityPartnerAddressId,
        placeholder:
          values?.docsForTenant?.files?.length > 0 ? 'upload more documents' : 'tap to upload',
        onViewDocument: () => {
          setDocumentViewModalArgs({
            navbarTitle: 'Documents for Tenant',
            hideGroupTitles: true,
            groups: [
              {
                title: 'Documents uploaded',
                items: values.docsForTenant.files.map((file, index) => ({
                  title: `Document ${index + 1}`,
                  value: file,
                })),
              },
            ],
            onClose: () => setDocumentViewModalArgs(null),
          });
        },
        onDeleteDocument: () => {
          setRemoveDocumentModalArgs({
            onConfirm: () => {
              setRemoveDocumentModalArgs(null);
              formikBag.setFieldValue('docsForTenant', []);
            },
            onCancel: () => setRemoveDocumentModalArgs(null),
          });
        },
      },
      validations: ['required'],
    },
    {
      id: 'requiredByUtilities',
      title: 'Which company needs documents?',
      label: (
        <SrText paddingTop="6px" fontSize="16px" font="greycliff" lineHeight="25px" display="block">
          {isHomebuyerPartner(utilityPartner)
            ? 'Instruct your client to submit the documents to specific companies.'
            : 'Instruct the tenant to submit the documents to specific companies.'}
        </SrText>
      ),
      type: 'info_checkbox_multi',
      properties: {
        choices: utilityTypes
          .filter(({ id }) => id !== UTILITY_TYPE_ID_INTERNET)
          .map((utilityType) => ({
            value: utilityType.id,
            label: (
              <SrFlex align="center" columnGap="5px">
                <SrBox marginLeft="5px">
                  <SrText
                    weight="800"
                    fontSize="16px"
                    font="greycliff"
                    lineHeight="25px"
                    display="block"
                    color={
                      values.requiredByUtilities?.includes(utilityType.id)
                        ? 'green'
                        : 'blueGreycliff'
                    }
                  >
                    {utilityType.label}
                  </SrText>
                </SrBox>
                <SrBox
                  width="20px"
                  height="20px"
                  backgroundImage={`url("${utilityType.iconUrl}")`}
                  backgroundSize="cover"
                  marginRight="5px"
                />
              </SrFlex>
            ),
          })),
      },
      validations: ['required'],
    },
    {
      id: 'noteToTenant',
      title: isHomebuyerPartner(utilityPartner) ? 'Note to client' : 'Note to tenant',
      label: '(optional)',
      type: 'long_text',
      properties: {
        placeholder: isHomebuyerPartner(utilityPartner)
          ? 'Write notes about the documents that you want the client to see...'
          : 'Write notes about the documents that you want the tenant to see...',
      },
    },
  ];
};

const SrUtilityProfitRequiredDocsModal = ({
  address,
  requiredDocs,
  utilityTypes,
  utilityPartner,
  utilityPartnerAddressId,
  onSave,
  onCancel,
}) => {
  const [documentViewModalArgs, setDocumentViewModalArgs] = useState(null);
  const [removeDocumentModalArgs, setRemoveDocumentModalArgs] = useState(null);

  const lastDoc = requiredDocs ? requiredDocs[requiredDocs?.length - 1] : [];

  const initialValues = {
    docsForTenant: {
      uploadDate: lastDoc ? lastDoc.createdAt : '',
      files: requiredDocs?.map((doc) => doc.url),
    },
    requiredByUtilities: lastDoc ? lastDoc.requiredByUtilityTypeIds : [],
    noteToTenant: lastDoc ? lastDoc.noteToTenant : '',
  };

  return (
    <>
      <SrDarkModal onClose={onCancel} className="SrUtilityProfitRequiredDocsModal">
        <SrModalContent style={{ width: 412, marginTop: 4 }}>
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <SrText
              weight="800"
              font="greycliff"
              fontSize="25px"
              lineHeight="30px"
              letterSpacing="0.5"
            >
              {isHomebuyerPartner(utilityPartner)
                ? 'Add docs for your client'
                : 'Add docs for the tenant'}
            </SrText>
            <SrBox marginTop="12px">
              <SrText
                weight="800"
                fontSize="16px"
                font="greycliff"
                lineHeight="19px"
                letterSpacing="0.5"
                display="block"
              >
                {address}
              </SrText>
            </SrBox>
            <SrBox width="328px" marginTop="7px">
              <SrText
                weight="600"
                fontSize="16px"
                font="greycliff"
                lineHeight="25px"
                display="block"
                align="center"
              >
                {isHomebuyerPartner(utilityPartner)
                  ? 'Share documents with clients so they can prove their residency to the utility company.'
                  : 'Share documents with tenants so they can prove their residency to the utility company.'}
              </SrText>
            </SrBox>
          </SrBox>
          <SrSeparator marginTop="20px" marginBottom="8px" />
          <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            <Formik
              initialValues={createInitialValues({
                formFields: getFormValues({
                  utilityPartner,
                  utilityPartnerAddressId,
                  utilityTypes,
                  setDocumentViewModalArgs,
                  setRemoveDocumentModalArgs,
                }),
                defaultValues: initialValues,
              })}
              validate={(formValues) => {
                return createFormValidationFn(
                  getFormValues({
                    utilityPartner,
                    utilityPartnerAddressId,
                    utilityTypes,
                    values: formValues,
                  }),
                )(formValues);
              }}
              onSubmit={onSave}
              render={(formikBag) => {
                const formFields = getFormValues({
                  utilityPartner,
                  utilityPartnerAddressId,
                  utilityTypes,
                  setDocumentViewModalArgs,
                  setRemoveDocumentModalArgs,
                  values: formikBag.values,
                  formikBag,
                });

                return (
                  <Form style={{ width: '100%', maxWidth: '350px' }}>
                    <SrBox margin="22px 0">
                      {formFields.map((formField) => {
                        return (
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                            className={SR_FIELD_THEME_ADOBE}
                          />
                        );
                      })}
                    </SrBox>
                    <SrBox className="submitButtonContainer">
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        disabled={!formikBag.isValid}
                        hasBoxShadow
                      >
                        Save
                      </SrButton>
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_GREY}
                        type="button"
                        margin="12px 0px"
                        onClick={onCancel}
                        hasBoxShadow
                      >
                        Cancel
                      </SrButton>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrModalContent>
      </SrDarkModal>
      {documentViewModalArgs && <SrModalDocsSplitView {...documentViewModalArgs} />}
      {removeDocumentModalArgs && (
        <SrUtilityProfitDeleteDocsConfirmationModal {...removeDocumentModalArgs} />
      )}
    </>
  );
};

SrUtilityProfitRequiredDocsModal.propTypes = {
  address: PropTypes.string.isRequired,
  requiredDocs: PropTypes.array.isRequired,
  utilityPartner: PropTypes.object,
  utilityPartnerAddressId: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SrUtilityProfitRequiredDocsModal.displayName = 'SrUtilityProfitRequiredDocsModal';

export default SrUtilityProfitRequiredDocsModal;
